import { ChevronRight, Trash2, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

interface CardRowProps {
  onClick: () => void;
  children: ReactNode;
}

/** Clickable card row shell (hover ring + trailing chevron). */
export function CardRow({ onClick, children }: CardRowProps) {
  return (
    <div
      onClick={onClick}
      className="group flex items-center gap-4 bg-white rounded-xl px-5 py-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.005] active:scale-[0.995] border border-transparent hover:border-emerald-100"
    >
      {children}
      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-500 shrink-0 transition-colors" />
    </div>
  );
}

interface CardAvatarProps {
  /** Initials to display; falls back to the icon when empty. */
  initials?: string | null;
  fallbackIcon: LucideIcon;
}

/** Round avatar of a card row: initials, or the domain icon. */
export function CardAvatar({ initials, fallbackIcon: Icon }: CardAvatarProps) {
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 group-hover:bg-emerald-50 transition-colors">
      {initials ? (
        <span className="text-sm font-semibold text-slate-600 group-hover:text-emerald-700">
          {initials}
        </span>
      ) : (
        <Icon className="h-4 w-4 text-slate-400 group-hover:text-emerald-600" />
      )}
    </div>
  );
}

interface CardRowDeleteButtonProps {
  /** Accessible name, e.g. "Supprimer la commande ORD-…". */
  label: string;
  onClick: () => void;
}

/** Trailing delete action of a card row; does not open the row. */
export function CardRowDeleteButton({ label, onClick }: CardRowDeleteButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className="shrink-0 rounded-lg p-2 text-slate-300 transition-colors hover:bg-red-50 hover:text-red-600 cursor-pointer"
    >
      <Trash2 className="h-4 w-4" />
    </button>
  );
}

interface CardListEmptyProps {
  icon: LucideIcon;
  title?: string;
  hint?: string;
}

/** Empty state of a card list. */
export function CardListEmpty({
  icon: Icon,
  title = 'Aucun résultat',
  hint = 'Essayez de modifier vos filtres',
}: CardListEmptyProps) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-slate-400">
      <Icon className="h-10 w-10 mb-3 text-slate-300" />
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs mt-1">{hint}</p>
    </div>
  );
}
